import { useMemo, useState } from 'react';
import type { ColumnDef } from '@tanstack/react-table';
import { Button, Tooltip } from '@heroui/react';
import { DataTable, type ColumnMetaExt } from '@/components/common/DataTable';
import { StatusChip } from '@/components/common/StatusChip';
import { ConfirmDialog } from '@/components/common/ConfirmDialog';
import { DnsRecordModal } from '@/features/domains/components/DnsRecordModal';
import { useZoneRecords, useDeleteZoneRecord } from '@/features/domains/hooks';
import type { RecordOrigin, ZoneRecord } from '@/features/domains/types';
import { PencilSquareIcon, TrashIcon, LockClosedIcon, PlusIcon } from '@/icons';

/**
 * Managed-DNS zone editor: the domain's editable records plus the platform's
 * locked system rows (MX/SPF/DKIM/DMARC). Reuses DataTable, StatusChip,
 * ConfirmDialog and the RHF+Zod record modal. Only rendered in managed mode.
 */
const ORIGIN: Record<Exclude<RecordOrigin, 'system'>, { label: string; tip: string; tone: 'info' | 'neutral' }> = {
  generated: { label: 'Nifty', tip: 'Generated by Nifty for mail (MX, SPF, DKIM, DMARC, MTA-STS, verification)', tone: 'info' },
  imported: { label: 'Imported', tip: 'Copied from the DNS host the domain used before', tone: 'neutral' },
  custom: { label: 'Custom', tip: 'Added in this dashboard', tone: 'neutral' },
};

function OriginChip({ record }: { record: ZoneRecord }) {
  if (record.systemManaged || record.origin === 'system') {
    return <Tooltip content="System-managed record"><span className="inline-flex"><StatusChip tone="neutral"><LockClosedIcon className="mr-1 h-3 w-3" />System</StatusChip></span></Tooltip>;
  }
  const o = ORIGIN[record.origin ?? 'custom'];
  return <Tooltip content={o.tip}><span className="inline-flex"><StatusChip tone={o.tone}>{o.label}</StatusChip></span></Tooltip>;
}

export function DnsRecordsTable({ domainId }: { domainId: string }) {
  const { data, isLoading, isError, refetch } = useZoneRecords(domainId);
  const del = useDeleteZoneRecord(domainId);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<ZoneRecord | null>(null);
  const [confirm, setConfirm] = useState<ZoneRecord | null>(null);

  const openCreate = () => { setEditing(null); setModalOpen(true); };
  const openEdit = (r: ZoneRecord) => { setEditing(r); setModalOpen(true); };

  const columns = useMemo<ColumnDef<ZoneRecord, unknown>[]>(() => [
    { id: 'type', header: 'Type', cell: ({ row }) => <span className="font-mono text-xs font-semibold text-fg">{row.original.type}</span> },
    { id: 'host', header: 'Host', cell: ({ row }) => <span className="font-mono text-xs text-fg">{row.original.name}</span> },
    { id: 'value', header: 'Value', cell: ({ row }) => (
      <Tooltip content={row.original.content}><span className="block max-w-[22rem] truncate font-mono text-xs text-fg-muted">{row.original.content}</span></Tooltip>
    ) },
    { id: 'priority', header: 'Priority', meta: { className: 'text-center' } as ColumnMetaExt, cell: ({ row }) => <span className="font-mono text-xs text-fg-muted">{row.original.priority ?? '—'}</span> },
    { id: 'ttl', header: 'TTL', cell: ({ row }) => <span className="font-mono text-xs text-fg-muted">{row.original.ttl}s</span> },
    { id: 'origin', header: 'Origin', cell: ({ row }) => (
      <div className="flex flex-wrap items-center gap-1">
        <OriginChip record={row.original} />
        {row.original.dirty && <Tooltip content="Not published yet"><span className="inline-flex"><StatusChip tone="warning">Draft</StatusChip></span></Tooltip>}
      </div>
    ) },
    { id: 'actions', header: '', meta: { className: 'text-right' } as ColumnMetaExt, cell: ({ row }) => row.original.systemManaged ? null : (
      <div className="flex justify-end gap-1" onClick={(e) => e.stopPropagation()}>
        <Tooltip content="Edit"><Button isIconOnly size="sm" variant="light" aria-label="Edit record" onPress={() => openEdit(row.original)}><PencilSquareIcon className="h-4 w-4" /></Button></Tooltip>
        <Tooltip content="Delete"><Button isIconOnly size="sm" variant="light" color="danger" aria-label="Delete record" onPress={() => setConfirm(row.original)}><TrashIcon className="h-4 w-4" /></Button></Tooltip>
      </div>
    ) },
  ], []);

  return (
    <div className="rounded-xl border border-border bg-surface">
      <div className="flex items-center justify-between border-b border-border p-4">
        <div>
          <h3 className="text-sm font-semibold text-fg">Zone records</h3>
          <p className="text-xs text-fg-muted">Every record in this zone. Nameserver records are locked; drafts take effect when the zone is published.</p>
        </div>
        <Button color="primary" size="sm" startContent={<PlusIcon className="h-4 w-4" />} onPress={openCreate}>Add record</Button>
      </div>
      <div className="overflow-x-auto">
        <DataTable<ZoneRecord>
          columns={columns} data={data ?? []} getRowId={(r) => r.id}
          isLoading={isLoading} isError={isError} onRetry={refetch}
          emptyMessage="No custom records yet — the platform records above keep mail flowing."
        />
      </div>

      <DnsRecordModal isOpen={modalOpen} onClose={() => setModalOpen(false)} domainId={domainId} record={editing} />
      <ConfirmDialog
        isOpen={!!confirm} onClose={() => setConfirm(null)}
        onConfirm={() => { if (confirm) del.mutate(confirm.id, { onSuccess: () => setConfirm(null) }); }}
        title="Delete DNS record?"
        body={confirm ? <>Delete the <b>{confirm.type}</b> record for <span className="font-mono">{confirm.name}</span>? This becomes effective after the next publish.</> : null}
        confirmLabel="Delete" tone="danger" isLoading={del.isPending}
      />
    </div>
  );
}
